/**
 * <personalization-customizer>
 *
 * Lets a customer add personalized text to a product, either:
 * - "deboss" mode: text only (the text gets engraved/recessed into the item).
 * - "inlay" mode: text plus a colour choice, sourced live from this
 *   product's own existing Color option values (multi-colour printing).
 *
 * Which mode applies (or whether the block shows at all) is driven by a
 * variant option, via the shared VariantGatedElement base class — the same
 * pattern used by <lithophane-customizer>.
 */
class PersonalizationCustomizer extends VariantGatedElement {
  constructor() {
    super();
    this.formId = this.dataset.formId;
    this.textPropertyName = this.dataset.textPropertyName || 'Personalization Text';
    this.inlayColorPropertyName = this.dataset.inlayColorPropertyName || 'Inlay Colour';
    this.characterLimit = parseInt(this.dataset.characterLimit, 10) || 20;
    this.required = this.dataset.required !== 'false';
    this.debossValue = this.dataset.debossValue || '';
    this.inlayValue = this.dataset.inlayValue || '';
    this.mode = undefined;

    this.form = document.getElementById(this.formId);
    this.submitButton = this.form ? this.form.querySelector('[type="submit"].product-form__submit') : null;

    this.textInput = this.querySelector('[data-personalization-text-input]');
    this.counterEl = this.querySelector('[data-personalization-counter]');
    this.statusEl = this.querySelector('[data-personalization-status]');
    this.textOutput = this.querySelector('[data-personalization-text-output]');
    this.colorOutput = this.querySelector('[data-personalization-color-output]');
    this.colorField = this.querySelector('[data-personalization-color-field]');
    this.colorInputs = Array.from(this.querySelectorAll('[data-personalization-color-input]'));
    this.debossHelp = this.querySelector('[data-personalization-help-deboss]');
    this.inlayHelp = this.querySelector('[data-personalization-help-inlay]');

    if (this.textInput) {
      this.textInput.setAttribute('maxlength', String(this.characterLimit));
      this.textInput.addEventListener('input', () => this.onTextInput());
    }
    this.colorInputs.forEach((input) => input.addEventListener('change', () => this.onColorChange()));

    // Optional extra choices (e.g. Text layout), each shown for 'both',
    // 'deboss' or 'inlay'. The picked value is copied to a hidden property.
    this.choiceFields = Array.from(this.querySelectorAll('[data-personalization-choice]')).map((field) => {
      const inputs = Array.from(field.querySelectorAll('[data-personalization-choice-input]'));
      const output = field.querySelector('[data-personalization-choice-output]');
      const sync = () => {
        const checked = inputs.find((input) => input.checked);
        if (output) output.value = checked ? checked.value : '';
      };
      inputs.forEach((input) => input.addEventListener('change', sync));
      sync();
      return { field, inputs, output, showFor: field.dataset.showFor || 'both' };
    });

    this.onTextInput();
    this.onColorChange();

    this.initVariantGate();
  }

  onGateValueChange(value) {
    let mode = 'none';
    if (!this.toggleViaVariant) {
      mode = 'deboss';
    } else if (value === this.debossValue) {
      mode = 'deboss';
    } else if (value === this.inlayValue) {
      mode = 'inlay';
    }
    this.setMode(mode);
  }

  setMode(mode) {
    const changed = this.mode !== mode;
    this.mode = mode;
    this.hidden = mode === 'none';

    const showColor = mode === 'inlay' && !!this.colorField;
    if (this.colorField) this.colorField.hidden = !showColor;
    if (this.debossHelp) this.debossHelp.hidden = mode !== 'deboss';
    if (this.inlayHelp) this.inlayHelp.hidden = mode !== 'inlay';

    if (this.textInput) this.textInput.disabled = mode === 'none';
    if (this.textOutput) this.textOutput.disabled = mode === 'none';
    this.colorInputs.forEach((input) => {
      input.disabled = !showColor;
    });
    if (this.colorOutput) this.colorOutput.disabled = !showColor;

    this.choiceFields.forEach(({ field, inputs, output, showFor }) => {
      const show = mode !== 'none' && (showFor === 'both' || showFor === mode);
      field.hidden = !show;
      inputs.forEach((input) => {
        input.disabled = !show;
      });
      if (output) output.disabled = !show;
    });

    if (changed) this.updateSubmitState();
  }

  onTextInput() {
    const value = this.textInput ? this.textInput.value.slice(0, this.characterLimit) : '';
    if (this.textOutput) this.textOutput.value = value;
    if (this.counterEl) this.counterEl.textContent = `${value.length} / ${this.characterLimit}`;
    this.updateSubmitState();
  }

  onColorChange() {
    const checked = this.colorInputs.find((input) => input.checked);
    if (this.colorOutput) this.colorOutput.value = checked ? checked.value : '';
    this.updateSubmitState();
  }

  updateSubmitState() {
    if (this.mode === 'none') {
      if (this.submitButton && this.submitButton.dataset.personalizationDisabled === 'true') {
        this.submitButton.disabled = false;
        delete this.submitButton.dataset.personalizationDisabled;
      }
      this.setStatus('', 'ok');
      return;
    }

    const hasText = !!(this.textOutput && this.textOutput.value.trim());
    const hasColor = this.mode !== 'inlay' || !!(this.colorOutput && this.colorOutput.value);
    const complete = hasText && hasColor;

    if (this.submitButton && this.required) {
      this.submitButton.disabled = !complete;
      this.submitButton.dataset.personalizationDisabled = String(!complete);
    }

    if (!hasText) {
      this.setStatus('Enter your personalization text to continue.', 'error');
    } else if (!hasColor) {
      this.setStatus('Choose an inlay colour to continue.', 'error');
    } else {
      this.setStatus('Looks good.', 'ok');
    }
  }

  setStatus(message, state) {
    if (!this.statusEl) return;
    this.statusEl.textContent = message;
    this.statusEl.dataset.state = state;
  }
}

customElements.define('personalization-customizer', PersonalizationCustomizer);
